// This file contains polyfills to support older browsers

// Import polyfills for IE11 and older browsers
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

// Import core-js polyfills for ES6+ features
import 'core-js/stable';

// Import regenerator-runtime for async/await support
import 'regenerator-runtime/runtime';

/**
 * Safari 16 and below don't support named capture groups in regular expressions
 * This polyfill helps prevent the "Invalid regular expression: invalid group specifier name" error
 */

// Function to safely create a RegExp that works in older browsers
function createSafeRegExp(pattern: string | RegExp, flags?: string): RegExp {
  if (pattern instanceof RegExp) {
    const newFlags = flags || (pattern.global ? 'g' : '') + 
                             (pattern.ignoreCase ? 'i' : '') + 
                             (pattern.multiline ? 'm' : '') + 
                             (pattern.dotAll ? 's' : '') + 
                             (pattern.unicode ? 'u' : '') + 
                             (pattern.sticky ? 'y' : '');
    return createSafeRegExp(pattern.source, newFlags);
  }
  
  if (typeof pattern !== 'string') {
    return new RegExp(pattern, flags);
  }
  
  try {
    // First try to create the regex normally
    return new RegExp(pattern, flags);
  } catch (e) {
    if (e instanceof SyntaxError) {
      try {
        // Try to fix named capture groups
        const namedGroupPattern = /\(\?<([^>]+)>/g;
        if (namedGroupPattern.test(pattern)) {
          // Replace named capture groups with non-capturing groups
          const safePattern = pattern.replace(namedGroupPattern, '(?:');
          console.warn('RegExp with named capture groups converted for browser compatibility:', 
                      { original: pattern, converted: safePattern });
          return new RegExp(safePattern, flags);
        }
      } catch (innerError) {
        console.error('Failed to fix RegExp pattern:', pattern, innerError);
      }
    }
    
    // If all else fails, return a simple regex that matches nothing
    console.error('RegExp creation failed, using fallback pattern:', pattern, e);
    return new RegExp('(?!)');
  }
}

// Patch the global RegExp constructor
const originalRegExpConstructor = RegExp;

// Only apply the patch if needed (Safari 16 and below)
try {
  // Test if the browser supports named capture groups
  new RegExp('(?<name>x)');
} catch (e) {
  // @ts-ignore - We need to override the RegExp constructor
  window.RegExp = function(pattern, flags) {
    return createSafeRegExp(pattern, flags);
  };
  
  // Copy all properties from the original RegExp constructor
  for (const prop in originalRegExpConstructor) {
    if (Object.prototype.hasOwnProperty.call(originalRegExpConstructor, prop)) {
      // @ts-ignore - Dynamic property assignment
      window.RegExp[prop] = originalRegExpConstructor[prop];
    }
  }
  
  // Set prototype and constructor properties correctly
  // @ts-ignore - We need to set the prototype
  window.RegExp.prototype = originalRegExpConstructor.prototype;
  // @ts-ignore - We need to set the constructor
  window.RegExp.prototype.constructor = window.RegExp;
  
  console.info('RegExp constructor patched for Safari 16 compatibility');
}

// Add a basic groups property to RegExp match results if not supported
if (!('groups' in RegExp.prototype)) {
  const originalExec = RegExp.prototype.exec;
  RegExp.prototype.exec = function(str) {
    const result = originalExec.call(this, str);
    if (result) {
      result.groups = {};
    }
    return result;
  };
}

// Add any additional browser-specific fixes here

// Fix for browsers not supporting CSS variables in SVG attributes
;(function() {
  // Run this code for all browsers since multiple browsers have this issue

  // Store original createElement method
  const originalCreateElement = document.createElement;
  const originalCreateElementNS = document.createElementNS;

  // Override createElement to intercept SVG creation
  document.createElement = function(tagName: string, options?: ElementCreationOptions): HTMLElement {
    const element = originalCreateElement.call(document, tagName, options);
    if (tagName.toLowerCase() === 'svg') {
      patchSvgElement(element as SVGElement);
    }
    return element;
  };

  // Override createElementNS to intercept SVG creation
  // @ts-ignore - We need to override the createElementNS method
  document.createElementNS = function(namespaceURI: string, qualifiedName: string): Element {
    // @ts-ignore - Call the original method
    const element = originalCreateElementNS.call(document, namespaceURI, qualifiedName);
    if (qualifiedName.toLowerCase() === 'svg' || namespaceURI === 'http://www.w3.org/2000/svg') {
      patchSvgElement(element as SVGElement);
    }
    return element;
  };

  // Function to patch SVG elements
  function patchSvgElement(svg: SVGElement): void {
    // Store original setAttribute method
    const originalSetAttribute = svg.setAttribute;

    // Override setAttribute to intercept CSS variables
    svg.setAttribute = function(name: string, value: string): void {
      // If the attribute value contains CSS variables, convert them to fixed values
      if (typeof value === 'string' && value.includes('calc(') && value.includes('var(--mantine-scale)')) {
        const fixedValue = convertCalcToFixed(value);
        originalSetAttribute.call(this, name, fixedValue || '');
      } else {
        originalSetAttribute.call(this, name, value);
      }
    };
  }

  // Function to convert CSS calc expressions to fixed values
  function convertCalcToFixed(value: string): string {
    if (!value) return value;
    
    // Extract values from calc expressions like calc(0.75rem * var(--mantine-scale))
    if (value.includes('calc(') && value.includes('var(--mantine-scale)')) {
      // Get the scale value from CSS
      const scale = getComputedStyle(document.documentElement).getPropertyValue('--mantine-scale') || '1';
      
      // Extract the rem value
      const remMatch = value.match(/calc\(([\d.]+)rem\s*\*\s*var/i);
      if (remMatch && remMatch[1]) {
        const remValue = parseFloat(remMatch[1]);
        // Convert to pixels (1rem = 16px by default)
        return `${remValue * 16 * parseFloat(scale)}px`;
      }
    }
    
    return value;
  }

  // Also patch existing SVG elements
  document.addEventListener('DOMContentLoaded', () => {
    // Find all SVG elements
    const svgElements = document.querySelectorAll('svg');
    
    // Patch each SVG element
    Array.from(svgElements).forEach((svg: SVGElement) => {
      patchSvgElement(svg);
      
      // Fix existing attributes
      ['width', 'height', 'x', 'y', 'cx', 'cy', 'r', 'rx', 'ry'].forEach(attr => {
        const value = svg.getAttribute(attr);
        if (value && value.includes('calc(') && value.includes('var(--mantine-scale)')) {
          svg.setAttribute(attr, convertCalcToFixed(value));
        }
      });
    });
  });
})();

// Fix for older browsers that don't support NodeList.forEach
if (window.NodeList && !NodeList.prototype.forEach) {
  // Using type assertion to avoid TypeScript errors
  NodeList.prototype.forEach = function(callback, thisArg) {
    thisArg = thisArg || window;
    for (let i = 0; i < this.length; i++) {
      callback.call(thisArg, this[i], i, this);
    }
  };
}

// Fix for older browsers that don't support Element.matches
if (!Element.prototype.matches) {
  // Using type assertion to avoid TypeScript errors
  Element.prototype.matches = function(selector) {
    const element = this as Element;
    const doc = element.ownerDocument;
    const elements = doc.querySelectorAll(selector);
    let i = 0;
    while (elements[i] && elements[i] !== element) {
      i++;
    }
    return Boolean(elements[i]);
  };
}
